/**
 * The agreement in force and its history (TMMPDM-0042). Its own read, so a failure stays here.
 * The plan name links to the reseller's Commission plans tab.
 */
import { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState, ErrorState } from '@/components/page';
import { Amount, stickyTableContainerSx } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import type {
  AffiliateAgreementHistory,
  AffiliateAgreementView,
  PayoutCadence,
  PayoutMethod,
  TaxDocsStatus,
} from '@/api/tmm/types';
import { formatDate } from '@/format';
import { TaxIdChip } from '../resellers/TaxIdChip';
import { CommissionPlanLabel } from './affiliateShared';

const CADENCE_FALLBACK: Record<PayoutCadence, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

const METHOD_FALLBACK: Record<PayoutMethod, string> = {
  MANUAL: 'Manual',
  STRIPE_CONNECT_MANUAL: 'Stripe Connect (manual payouts)',
  STRIPE_CONNECT: 'Stripe Connect (automatic payouts)',
};

const TAX_FALLBACK: Record<TaxDocsStatus, string> = {
  NOT_REQUIRED: 'Not required',
  PENDING: 'Pending',
  COLLECTED: 'Collected',
};

interface AffiliateAgreementSectionProps {
  slug: string;
  affiliateId: number;
  /** The tenant's reseller, for the plan link. Null when the tenant has none. */
  resellerId: number | null;
  /** The tenant's currency — the agreement view does not carry one. */
  currency: string;
}

export function AffiliateAgreementSection({
  slug,
  affiliateId,
  resellerId,
  currency,
}: AffiliateAgreementSectionProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [reloadToken, setReloadToken] = useState(0);
  const [history, setHistory] = useState<AffiliateAgreementHistory | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getAffiliateAgreement(slug, affiliateId)
      .then((body) => {
        if (cancelled) return;
        setHistory(body);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setHistory(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, affiliateId, reloadToken]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('affiliates.agreement.loadError', 'Could not load the agreement.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }
  if (history === null) return <LoadingSpinner />;

  return (
    <Stack spacing={2}>
      {history.inForce === null ? (
        <EmptyState surface title={t('affiliates.agreement.none', 'No agreement')} />
      ) : (
        <AgreementTerms
          agreement={history.inForce}
          resellerId={resellerId}
          currency={currency}
          locale={i18n.language}
          ariaLabel={t('affiliates.agreement.inForceAria', 'Agreement in force')}
        />
      )}
      <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('affiliates.agreement.history', 'History')}
        </AccordionSummary>
        <AccordionDetails>
          {history.history.length === 0 ? (
            <EmptyState title={t('affiliates.agreement.none', 'No agreement')} />
          ) : (
            <AgreementTable
              rows={history.history}
              currency={currency}
              locale={i18n.language}
              ariaLabel={t('affiliates.agreement.historyAria', 'Agreement history')}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function AgreementTerms({
  agreement,
  resellerId,
  currency,
  locale,
  ariaLabel,
}: {
  agreement: AffiliateAgreementView;
  resellerId: number | null;
  currency: string;
  locale: string;
  ariaLabel: string;
}) {
  return (
    <Stack component="section" spacing={1} aria-label={ariaLabel}>
      <PlanHeading plan={agreement.commissionPlan} resellerId={resellerId} />
      <TermsLine agreement={agreement} currency={currency} locale={locale} />
    </Stack>
  );
}

/** The plan's name links to the reseller's Commission plans tab; chips stay the server's. */
function PlanHeading({
  plan,
  resellerId,
}: {
  plan: AffiliateAgreementView['commissionPlan'];
  resellerId: number | null;
}) {
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
      {resellerId === null ? (
        <span>{plan.name}</span>
      ) : (
        <Link
          component={RouterLink}
          to={`/partners/${resellerId}?tab=commissionPlans`}
          underline="hover"
        >
          {plan.name}
        </Link>
      )}
      <CommissionPlanLabel plan={plan} omitName />
    </Stack>
  );
}

function TermsLine({
  agreement,
  currency,
  locale,
}: {
  agreement: AffiliateAgreementView;
  currency: string;
  locale: string;
}) {
  const { t } = useCopy();
  const absent = t('affiliates.absent', '—');
  return (
    <Typography variant="body2" color="text.secondary" component="div">
      {t(
        `resellers.agreement.cadence.${agreement.payoutCadence}`,
        CADENCE_FALLBACK[agreement.payoutCadence],
      )}
      {' · '}
      <Amount minor={agreement.minPayoutMinor} currency={currency} locale={locale} />
      {' · '}
      {t(`payouts.method.${agreement.payoutMethod}`, METHOD_FALLBACK[agreement.payoutMethod])}
      {' · '}
      {t(
        `resellers.agreement.taxDocs.${agreement.taxDocsStatus}`,
        TAX_FALLBACK[agreement.taxDocsStatus],
      )}
      {/* TMMAPI-0161: the tax ID status as sent, beside the tax documents; null or absent shows nothing. */}
      {agreement.taxIdStatus != null ? (
        <>
          {' · '}
          {t('resellers.agreement.taxIdLabel', 'Tax ID')}{' '}
          <TaxIdChip status={agreement.taxIdStatus} />
        </>
      ) : null}
      {' · '}
      {t('affiliates.dates.effective', 'Effective {{date}}', {
        date: formatDate(agreement.effectiveFrom, locale),
      })}
      {' · '}
      {t('affiliates.dates.endedOn', 'Ended {{date}}', {
        date: agreement.endedAt !== undefined ? formatDate(agreement.endedAt, locale) : absent,
      })}
    </Typography>
  );
}

function AgreementTable({
  rows,
  currency,
  locale,
  ariaLabel,
}: {
  rows: AffiliateAgreementView[];
  currency: string;
  locale: string;
  ariaLabel: string;
}) {
  const { t } = useCopy();
  const absent = t('affiliates.absent', '—');
  return (
    <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
      <Table size="small" aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            <TableCell>{t('affiliates.col.plan', 'Plan')}</TableCell>
            <TableCell>{t('affiliates.col.effective', 'Effective')}</TableCell>
            <TableCell>{t('affiliates.col.ended', 'Ended')}</TableCell>
            <TableCell>{t('affiliates.col.cadence', 'Cadence')}</TableCell>
            <TableCell align="right">{t('affiliates.col.minimum', 'Minimum payout')}</TableCell>
            <TableCell>{t('affiliates.col.method', 'Method')}</TableCell>
            <TableCell>{t('affiliates.col.taxDocs', 'Tax documents')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                <CommissionPlanLabel plan={row.commissionPlan} />
              </TableCell>
              <TableCell>{formatDate(row.effectiveFrom, locale)}</TableCell>
              <TableCell>
                {row.endedAt !== undefined ? formatDate(row.endedAt, locale) : absent}
              </TableCell>
              <TableCell>
                {t(
                  `resellers.agreement.cadence.${row.payoutCadence}`,
                  CADENCE_FALLBACK[row.payoutCadence],
                )}
              </TableCell>
              <TableCell align="right">
                <Amount minor={row.minPayoutMinor} currency={currency} locale={locale} />
              </TableCell>
              <TableCell>
                {t(`payouts.method.${row.payoutMethod}`, METHOD_FALLBACK[row.payoutMethod])}
              </TableCell>
              <TableCell>
                {t(
                  `resellers.agreement.taxDocs.${row.taxDocsStatus}`,
                  TAX_FALLBACK[row.taxDocsStatus],
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
